import React, { useMemo } from 'react';
import {
  Paper,
  Typography,
  Box,
  Card,
  CardContent,
  LinearProgress,
  Chip,
  Alert,
  AlertTitle,
} from '@mui/material';
import { MobilePageHeader } from './MobileOptimizations';
import { PieChart, Pie, Cell, LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer } from 'recharts';
import { Transaction, Category, MonthlyStats, Budget } from '../types';
import { formatCurrency, getCurrentMonth } from '../utils/dateUtils';
import BudgetAlerts from './BudgetAlerts';
import { generateBudgetRecommendations, calculateSavingsRate } from '../utils/budgetUtils';

interface DashboardProps {
  transactions: Transaction[];
  categories: Category[];
  budgets?: Budget[];
}

const Dashboard: React.FC<DashboardProps> = ({ transactions, categories, budgets = [] }) => {
  const currentMonth = getCurrentMonth();
  
  const monthlyStats = useMemo((): MonthlyStats => {
    const currentMonthTransactions = transactions.filter(t => {
      try {
        const date = t.date instanceof Date ? t.date : new Date(t.date);
        const transactionMonth = `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}`;
        return transactionMonth === currentMonth;
      } catch (error) {
        console.error('Erreur lors du traitement de la date:', error, t);
        return false;
      }
    });

    const totalIncome = currentMonthTransactions
      .filter(t => t.type === 'income')
      .reduce((sum, t) => sum + t.amount, 0);

    const totalExpenses = currentMonthTransactions
      .filter(t => t.type === 'expense')
      .reduce((sum, t) => sum + t.amount, 0);

    const balance = totalIncome - totalExpenses;
    const savingsRate = totalIncome > 0 ? (balance / totalIncome) * 100 : 0;

    return {
      totalIncome,
      totalExpenses,
      balance,
      savingsRate,
    };
  }, [transactions, currentMonth]);

  const expensesByCategory = useMemo(() => {
    const expenses = transactions.filter(t => t.type === 'expense');
    const categoryTotals = expenses.reduce((acc, t) => {
      acc[t.category] = (acc[t.category] || 0) + t.amount;
      return acc;
    }, {} as Record<string, number>);

    return Object.entries(categoryTotals).map(([name, value]) => {
      const category = categories.find(c => c.name === name);
      return {
        name,
        value,
        color: category?.color || '#8884d8',
      };
    });
  }, [transactions, categories]);

  const monthlyTrend = useMemo(() => {
    const monthlyData = transactions.reduce((acc, t) => {
      const month = `${t.date.getFullYear()}-${String(t.date.getMonth() + 1).padStart(2, '0')}`;
      if (!acc[month]) {
        acc[month] = { month, income: 0, expenses: 0 };
      }
      if (t.type === 'income') {
        acc[month].income += t.amount;
      } else {
        acc[month].expenses += t.amount;
      }
      return acc;
    }, {} as Record<string, { month: string; income: number; expenses: number }>);

    return Object.values(monthlyData).sort((a, b) => a.month.localeCompare(b.month));
  }, [transactions]);

  const budgetProgress = useMemo(() => {
    if (budgets.length === 0) return [];
    
    const currentMonth = new Date().getMonth();
    const currentYear = new Date().getFullYear();
    
    return budgets.map(budget => {
      const spent = transactions
        .filter(t => {
          const transactionDate = new Date(t.date);
          const isCorrectCategory = t.category === budget.category;
          const isExpense = t.type === 'expense';
          
          if (budget.period === 'monthly') {
            return isCorrectCategory && isExpense &&
                   transactionDate.getMonth() === currentMonth &&
                   transactionDate.getFullYear() === currentYear;
          } else {
            return isCorrectCategory && isExpense &&
                   transactionDate.getFullYear() === currentYear;
          }
        })
        .reduce((sum, t) => sum + t.amount, 0);

      const percentage = budget.allocated > 0 ? (spent / budget.allocated) * 100 : 0;
      
      return {
        ...budget,
        spent,
        remaining: budget.allocated - spent,
        percentage: Math.min(percentage, 100),
        isOverBudget: spent > budget.allocated,
      };
    });
  }, [budgets, transactions]);

  // Générer les alertes budgétaires
  const budgetAlerts = budgetProgress.filter(budget => 
    budget.percentage >= 80 || budget.isOverBudget
  );

  const creditAlert = transactions
    .filter(t => t.type === 'expense' && t.category === 'Crédit')
    .reduce((sum, t) => sum + t.amount, 0);

  // Générer les recommandations budgétaires
  const recommendations = generateBudgetRecommendations(transactions, budgets, categories);
  
  // Calculer le taux d'épargne amélioré
  const savingsRate = calculateSavingsRate(transactions);

  return (
    <Box>
      <MobilePageHeader 
        title="Dashboard" 
        subtitle={new Date().toLocaleDateString('fr-FR', { month: 'long', year: 'numeric' })}
      />
      
      {/* Alertes intelligentes de dépassement */}
      <BudgetAlerts 
        transactions={transactions} 
        budgets={budgets} 
        categories={categories} 
      />
      
      {/* Section Alertes */}
      {(budgetAlerts.length > 0 || creditAlert > 0) && (
        <Box sx={{ mb: 3 }}>
          <Typography variant="h6" gutterBottom>🚨 Alertes Budgétaires</Typography>
          <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))', gap: 2 }}>
            {budgetAlerts.map(budget => (
              <Alert 
                key={budget.id}
                severity={budget.isOverBudget ? "error" : "warning"}
                sx={{ mb: 1 }}
              >
                <AlertTitle>
                  {budget.isOverBudget ? "Budget dépassé" : "Attention au budget"} - {budget.category}
                </AlertTitle>
                Dépensé: {formatCurrency(budget.spent)} / {formatCurrency(budget.allocated)} 
                ({budget.percentage.toFixed(0)}%)
              </Alert>
            ))}
            {creditAlert > 0 && (
              <Alert severity="info" sx={{ mb: 1 }}>
                <AlertTitle>Suivi des crédits</AlertTitle>
                Total des remboursements ce mois: {formatCurrency(creditAlert)}
              </Alert>
            )}
          </Box>
        </Box>
      )}
      
      {/* Section Recommandations */}
      {recommendations.length > 0 && (
        <Box sx={{ mb: 3 }}>
          <Typography variant="h6" gutterBottom>💡 Conseils Budgétaires</Typography>
          <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(350px, 1fr))', gap: 2 }}>
            {recommendations.map((rec, index) => (
              <Alert 
                key={index}
                severity={rec.type}
                sx={{ mb: 1 }}
              >
                <AlertTitle>{rec.category}</AlertTitle>
                <Typography variant="body2" sx={{ mb: 1 }}>{rec.message}</Typography>
                {rec.action && (
                  <Typography variant="body2" sx={{ fontWeight: 'bold', color: 'inherit' }}>
                    ➡️ {rec.action}
                  </Typography>
                )}
              </Alert>
            ))}
          </Box>
        </Box>
      )}
      
      {/* Section Taux d'épargne */}
      <Box sx={{ mb: 3 }}>
        <Card sx={{ 
          background: savingsRate >= 20 ? 'linear-gradient(135deg, #10b981 0%, #059669 100%)' : 
                     savingsRate >= 10 ? 'linear-gradient(135deg, #f59e0b 0%, #d97706 100%)' : 
                     'linear-gradient(135deg, #ef4444 0%, #dc2626 100%)', 
          color: 'white',
          mb: 2
        }}>
          <CardContent>
            <Typography variant="h6" gutterBottom>💰 Taux d'épargne mensuel</Typography>
            <Typography variant="h4" sx={{ fontWeight: 'bold' }}>
              {savingsRate.toFixed(1)}%
            </Typography>
            <Typography variant="body2" sx={{ opacity: 0.9, mt: 1 }}>
              {savingsRate >= 20 ? 'Excellent! Vous êtes sur la bonne voie' :
               savingsRate >= 10 ? 'Bien, essayez d\'améliorer encore' :
               savingsRate >= 0 ? 'Attention, votre épargne est faible' :
               'Alerte: vous dépensez plus que vos revenus!'}
            </Typography>
          </CardContent>
        </Card>
      </Box>
      
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
        <Box sx={{ display: 'flex', gap: 3, flexWrap: 'wrap' }}>
          <Card sx={{ 
            background: 'linear-gradient(135deg, #10b981 0%, #059669 100%)', 
            color: 'white', 
            minWidth: 200, 
            flex: 1,
            position: 'relative',
            overflow: 'hidden'
          }}>
            <CardContent sx={{ position: 'relative', zIndex: 1 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1 }}>
                <Typography variant="h6" sx={{ fontWeight: 600, opacity: 0.9 }}>Revenus</Typography>
                <Box sx={{ 
                  width: 48, 
                  height: 48, 
                  borderRadius: '50%', 
                  backgroundColor: 'rgba(255, 255, 255, 0.2)', 
                  display: 'flex', 
                  alignItems: 'center', 
                  justifyContent: 'center',
                  fontSize: '1.5rem'
                }}>
                  📈
                </Box>
              </Box>
              <Typography variant="h4" sx={{ fontWeight: 700, mb: 1 }}>
                {formatCurrency(monthlyStats.totalIncome)}
              </Typography>
              <Typography variant="body2" sx={{ opacity: 0.8 }}>
                Ce mois-ci
              </Typography>
            </CardContent>
            <Box sx={{ 
              position: 'absolute', 
              top: 0, 
              right: 0, 
              width: 100, 
              height: 100, 
              borderRadius: '50%', 
              backgroundColor: 'rgba(255, 255, 255, 0.1)', 
              transform: 'translate(30px, -30px)' 
            }} />
          </Card>
          
          <Card sx={{ 
            background: 'linear-gradient(135deg, #ef4444 0%, #dc2626 100%)', 
            color: 'white', 
            minWidth: 200, 
            flex: 1,
            position: 'relative',
            overflow: 'hidden'
          }}>
            <CardContent sx={{ position: 'relative', zIndex: 1 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1 }}>
                <Typography variant="h6" sx={{ fontWeight: 600, opacity: 0.9 }}>Dépenses</Typography>
                <Box sx={{ 
                  width: 48, 
                  height: 48, 
                  borderRadius: '50%', 
                  backgroundColor: 'rgba(255, 255, 255, 0.2)', 
                  display: 'flex', 
                  alignItems: 'center', 
                  justifyContent: 'center',
                  fontSize: '1.5rem'
                }}>
                  📉
                </Box>
              </Box>
              <Typography variant="h4" sx={{ fontWeight: 700, mb: 1 }}>
                {formatCurrency(monthlyStats.totalExpenses)}
              </Typography>
              <Typography variant="body2" sx={{ opacity: 0.8 }}>
                Ce mois-ci
              </Typography>
            </CardContent>
            <Box sx={{ 
              position: 'absolute', 
              top: 0, 
              right: 0, 
              width: 100, 
              height: 100, 
              borderRadius: '50%', 
              backgroundColor: 'rgba(255, 255, 255, 0.1)', 
              transform: 'translate(30px, -30px)' 
            }} />
          </Card>
          
          <Card sx={{ 
            background: monthlyStats.balance >= 0 
              ? 'linear-gradient(135deg, #3b82f6 0%, #2563eb 100%)' 
              : 'linear-gradient(135deg, #f59e0b 0%, #d97706 100%)', 
            color: 'white', 
            minWidth: 200, 
            flex: 1,
            position: 'relative',
            overflow: 'hidden'
          }}>
            <CardContent sx={{ position: 'relative', zIndex: 1 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1 }}>
                <Typography variant="h6" sx={{ fontWeight: 600, opacity: 0.9 }}>Solde</Typography>
                <Box sx={{ 
                  width: 48, 
                  height: 48, 
                  borderRadius: '50%', 
                  backgroundColor: 'rgba(255, 255, 255, 0.2)', 
                  display: 'flex', 
                  alignItems: 'center', 
                  justifyContent: 'center',
                  fontSize: '1.5rem'
                }}>
                  {monthlyStats.balance >= 0 ? '💰' : '⚠️'}
                </Box>
              </Box>
              <Typography variant="h4" sx={{ fontWeight: 700, mb: 1 }}>
                {formatCurrency(monthlyStats.balance)}
              </Typography>
              <Typography variant="body2" sx={{ opacity: 0.8 }}>
                {monthlyStats.balance >= 0 ? 'Positif' : 'Négatif'}
              </Typography>
            </CardContent>
            <Box sx={{ 
              position: 'absolute', 
              top: 0, 
              right: 0, 
              width: 100, 
              height: 100, 
              borderRadius: '50%', 
              backgroundColor: 'rgba(255, 255, 255, 0.1)', 
              transform: 'translate(30px, -30px)' 
            }} />
          </Card>
          
          <Card sx={{ 
            background: 'linear-gradient(135deg, #06b6d4 0%, #0891b2 100%)', 
            color: 'white', 
            minWidth: 200, 
            flex: 1,
            position: 'relative',
            overflow: 'hidden'
          }}>
            <CardContent sx={{ position: 'relative', zIndex: 1 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1 }}>
                <Typography variant="h6" sx={{ fontWeight: 600, opacity: 0.9 }}>Taux d'épargne</Typography>
                <Box sx={{ 
                  width: 48, 
                  height: 48, 
                  borderRadius: '50%', 
                  backgroundColor: 'rgba(255, 255, 255, 0.2)', 
                  display: 'flex', 
                  alignItems: 'center', 
                  justifyContent: 'center',
                  fontSize: '1.5rem'
                }}>
                  🎯
                </Box>
              </Box>
              <Typography variant="h4" sx={{ fontWeight: 700, mb: 1 }}>
                {monthlyStats.savingsRate.toFixed(1)}%
              </Typography>
              <Typography variant="body2" sx={{ opacity: 0.8 }}>
                {monthlyStats.savingsRate > 20 ? 'Excellent' : monthlyStats.savingsRate > 10 ? 'Bien' : 'À améliorer'}
              </Typography>
            </CardContent>
            <Box sx={{ 
              position: 'absolute', 
              top: 0, 
              right: 0, 
              width: 100, 
              height: 100, 
              borderRadius: '50%', 
              backgroundColor: 'rgba(255, 255, 255, 0.1)', 
              transform: 'translate(30px, -30px)' 
            }} />
          </Card>
        </Box>

        {budgetProgress.length > 0 && (
      <Box sx={{ mb: 3 }}>
        <Typography variant="h6" gutterBottom sx={{ mt: 2 }}>
          Suivi des budgets
        </Typography>
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
          {budgetProgress.slice(0, 3).map((budget) => (
            <Paper key={budget.id} sx={{ p: 2 }}>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <Chip
                    label={budget.category}
                    size="small"
                    sx={{
                      backgroundColor: categories.find(c => c.name === budget.category)?.color || '#gray',
                      color: 'white',
                    }}
                      />
                      <Typography variant="body2" color="text.secondary">
                        {budget.period === 'monthly' ? 'Mensuel' : 'Annuel'}
                      </Typography>
                    </Box>
                    <Typography variant="body2" color={budget.isOverBudget ? 'error.main' : 'text.primary'}>
                      {formatCurrency(budget.spent)} / {formatCurrency(budget.allocated)}
                    </Typography>
                  </Box>
                  <LinearProgress
                    variant="determinate"
                    value={budget.percentage}
                    color={budget.isOverBudget ? 'error' : budget.percentage > 80 ? 'warning' : 'success'}
                    sx={{ height: 8, borderRadius: 4 }}
                  />
                  <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5, display: 'block' }}>
                    {budget.percentage.toFixed(1)}% utilisé
                    {budget.isOverBudget && (
                      <Typography component="span" color="error.main" fontWeight="bold">
                        {' '}(Dépassement: {formatCurrency(budget.spent - budget.allocated)})
                      </Typography>
                    )}
                  </Typography>
                </Paper>
              ))}
            </Box>
          </Box>
        )}

        <Box sx={{ display: 'flex', gap: 3, flexWrap: 'wrap' }}>
          <Paper sx={{ p: 2, flex: 1, minWidth: 400 }}>
            <Typography variant="h6" gutterBottom>
              Répartition des dépenses
            </Typography>
            <ResponsiveContainer width="100%" height={300}>
              <PieChart>
                <Pie
                  data={expensesByCategory}
                  cx="50%"
                  cy="50%"
                  labelLine={false}
                  label={({ name, percent }) => `${name} (${((percent || 0) * 100).toFixed(0)}%)`}
                  outerRadius={80}
                  fill="#8884d8"
                  dataKey="value"
                >
                  {expensesByCategory.map((entry, index) => (
                    <Cell key={`cell-${index}`} fill={entry.color} />
                  ))}
                </Pie>
                <Tooltip formatter={(value) => formatCurrency(value as number)} />
              </PieChart>
            </ResponsiveContainer>
          </Paper>

          <Paper sx={{ p: 2, flex: 1, minWidth: 400 }}>
            <Typography variant="h6" gutterBottom>
              Évolution mensuelle
            </Typography>
            <ResponsiveContainer width="100%" height={300}>
              <LineChart data={monthlyTrend}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis dataKey="month" />
                <YAxis />
                <Tooltip formatter={(value) => formatCurrency(value as number)} />
                <Line type="monotone" dataKey="income" stroke="#4caf50" name="Revenus" />
                <Line type="monotone" dataKey="expenses" stroke="#f44336" name="Dépenses" />
              </LineChart>
            </ResponsiveContainer>
          </Paper>
        </Box>
      </Box>
    </Box>
  );
};

export default Dashboard;